import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Head from "next/head";
import FeatcalApp from "@/components/ui/featcal-app";
import Image from "next/image";
import gsap from "gsap";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRegion } from "@/contexts/RegionContext";

type Step = "organization" | "contact" | "features";

type OrganizationType = "clinic" | "nutritionist" | "coach" | "other";

interface OrganizationInfo {
  name: string;
  type: OrganizationType;
  country: string;
  estimatedUsers: number;
}

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "Germany",
  "France",
  "Spain",
  "Italy",
  "Netherlands",
  "Belgium",
  "Switzerland",
  "Austria",
  "Sweden",
  "Norway",
  "Denmark",
  "Finland",
  "Other",
];

type FeatureType =
  | "nutrition"
  | "sleep"
  | "exercise"
  | "stress"
  | "recipes"
  | "ai_logging"
  | "consultation_notes";

export default function StartPage() {
  const router = useRouter();
  const { t, language, setLanguage } = useLanguage();
  const { region, isLoading: isRegionLoading } = useRegion();
  const [currentStep, setCurrentStep] = useState<Step>("organization");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [organizationData, setOrganizationData] = useState<OrganizationInfo>({
    name: "",
    type: "clinic",
    country: "",
    estimatedUsers: 10,
  });

  const [contactData, setContactData] = useState<ContactInfo>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [selectedFeatures, setSelectedFeatures] = useState<FeatureType[]>([
    "nutrition",
    "exercise",
  ]);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showLogoSkeleton, setShowLogoSkeleton] = useState(false);
  const [displayLogo, setDisplayLogo] = useState<string>("/images/logo.svg");
  const logoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);

  // Lead tracking
  const [leadId, setLeadId] = useState<string | null>(null);
  const leadCreatedRef = useRef<boolean>(false);

  // Refs for feature cards animations
  const nutritionCardRef = useRef<HTMLDivElement>(null);
  const sleepCardRef = useRef<HTMLDivElement>(null);
  const exerciseCardRef = useRef<HTMLDivElement>(null);
  const stressCardRef = useRef<HTMLDivElement>(null);
  const recipesCardRef = useRef<HTMLDivElement>(null);
  const aiLoggingCardRef = useRef<HTMLDivElement>(null);
  const consultationCardRef = useRef<HTMLDivElement>(null);

  // Track which cards have been animated
  const animatedCardsRef = useRef<Set<FeatureType>>(new Set());

  const handleNext = () => {
    if (currentStep === "organization") {
      if (!organizationData.name.trim()) {
        setError(t("start.organization.errors.nameRequired"));
        return;
      }
      if (!organizationData.country) {
        setError(t("start.organization.errors.countryRequired"));
        return;
      }
      setError(null);
      setCurrentStep("contact");
    } else if (currentStep === "contact") {
      if (!contactData.firstName.trim() || !contactData.lastName.trim()) {
        setError(t("start.contact.errors.nameRequired"));
        return;
      }
      if (!contactData.email.trim()) {
        setError(t("start.contact.errors.emailRequired"));
        return;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactData.email)) {
        setError(t("start.contact.errors.emailInvalid"));
        return;
      }
      setError(null);
      setCurrentStep("features");
    }
  };

  const handleBack = () => {
    setError(null);
    if (currentStep === "contact") {
      setCurrentStep("organization");
    } else if (currentStep === "features") {
      setCurrentStep("contact");
    }
  };

  const toggleFeature = (featureId: FeatureType) => {
    setSelectedFeatures((prev) =>
      prev.includes(featureId)
        ? prev.filter((f) => f !== featureId)
        : [...prev, featureId]
    );
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Clear any existing timeout
      if (logoTimeoutRef.current) {
        clearTimeout(logoTimeoutRef.current);
        logoTimeoutRef.current = null;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError(t("start.errors.imageFile"));
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(t("start.errors.fileSize"));
        return;
      }
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const newLogoPreview = reader.result as string;
        setLogoPreview(newLogoPreview);
        // Show skeleton for 1 second before displaying new logo
        setShowLogoSkeleton(true);
        logoTimeoutRef.current = setTimeout(() => {
          setDisplayLogo(newLogoPreview);
          setShowLogoSkeleton(false);
          logoTimeoutRef.current = null;
        }, 500);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  // Handle logo removal
  useEffect(() => {
    if (!logoPreview && logoFile === null) {
      // Clear any pending timeout
      if (logoTimeoutRef.current) {
        clearTimeout(logoTimeoutRef.current);
        logoTimeoutRef.current = null;
      }
      setDisplayLogo("/images/logo.svg");
      setShowLogoSkeleton(false);
    }
  }, [logoPreview, logoFile]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (logoTimeoutRef.current) {
        clearTimeout(logoTimeoutRef.current);
      }
    };
  }, []);

  // GSAP animations for feature cards
  useEffect(() => {
    // Only animate on features step
    if (currentStep !== "features") return;

    const animateCard = (
      featureId: FeatureType,
      cardRef: React.RefObject<HTMLDivElement | null>,
      direction: "left" | "right" | "top" | "bottom",
      delay: number = 0
    ) => {
      if (!cardRef.current) return;

      // Skip if already animated
      if (animatedCardsRef.current.has(featureId)) return;

      const card = cardRef.current;

      // Initial state
      gsap.set(card, {
        opacity: 0,
        scale: 0.8,
        x: direction === "left" ? -100 : direction === "right" ? 100 : 0,
        y: direction === "top" ? -100 : direction === "bottom" ? 100 : 0,
      });

      // Entrance animation
      gsap.to(card, {
        opacity: 1,
        scale: 1,
        x: 0,
        y: 0,
        duration: 0.6,
        delay: delay,
        ease: "back.out(1.7)",
        onComplete: () => {
          // Floating animation (levitation)
          gsap.to(card, {
            y: -10,
            duration: 2,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
          });

          // Breathing animation (scale pulse)
          gsap.to(card, {
            scale: 1.05,
            duration: 3,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
          });
        },
      });

      // Mark as animated
      animatedCardsRef.current.add(featureId);
    };

    const cardMap: Array<{
      id: FeatureType;
      ref: React.RefObject<HTMLDivElement | null>;
      direction: "left" | "right" | "top" | "bottom";
      delay: number;
    }> = [
      { id: "nutrition", ref: nutritionCardRef, direction: "right", delay: 0 },
      { id: "sleep", ref: sleepCardRef, direction: "left", delay: 0.1 },
      { id: "exercise", ref: exerciseCardRef, direction: "bottom", delay: 0.2 },
      { id: "stress", ref: stressCardRef, direction: "left", delay: 0.3 },
      { id: "recipes", ref: recipesCardRef, direction: "top", delay: 0.15 },
      {
        id: "ai_logging",
        ref: aiLoggingCardRef,
        direction: "left",
        delay: 0.25,
      },
      {
        id: "consultation_notes",
        ref: consultationCardRef,
        direction: "right",
        delay: 0.35,
      },
    ];

    // Animate newly selected cards
    cardMap.forEach(({ id, ref, direction, delay }) => {
      if (selectedFeatures.includes(id)) {
        animateCard(id, ref, direction, delay);
      }
    });

    // Remove from animated set if deselected
    cardMap.forEach(({ id, ref }) => {
      if (!selectedFeatures.includes(id)) {
        if (animatedCardsRef.current.has(id)) {
          animatedCardsRef.current.delete(id);
          if (ref.current) {
            gsap.killTweensOf(ref.current);
          }
        }
      }
    });
  }, [selectedFeatures, currentStep]);

  // Auto-create lead when organization name is filled
  useEffect(() => {
    const createInitialLead = async () => {
      // Only create if organization name is filled and we haven't created a lead yet
      if (organizationData.name.trim() && !leadCreatedRef.current) {
        try {
          const docRef = await addDoc(collection(db, "leads"), {
            organization: {
              name: organizationData.name,
              type: organizationData.type,
              country: organizationData.country,
              estimatedUsers: organizationData.estimatedUsers,
            },
            contact: {
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
            },
            features: [],
            logoUrl: null,
            createdAt: serverTimestamp(),
            status: "incomplete",
          });
          setLeadId(docRef.id);
          leadCreatedRef.current = true;
        } catch (err) {
          console.error("Error creating initial lead:", err);
        }
      }
    };

    // Debounce the creation to avoid multiple calls while typing
    const timeoutId = setTimeout(createInitialLead, 1000);
    return () => clearTimeout(timeoutId);
  }, [
    organizationData.name,
    organizationData.type,
    organizationData.country,
    organizationData.estimatedUsers,
  ]);

  // Update lead with contact info when it changes
  useEffect(() => {
    const updateLeadWithContact = async () => {
      if (
        leadId &&
        (contactData.firstName.trim() ||
          contactData.lastName.trim() ||
          contactData.email.trim())
      ) {
        try {
          await updateDoc(doc(db, "leads", leadId), {
            contact: {
              firstName: contactData.firstName,
              lastName: contactData.lastName,
              email: contactData.email,
              phone: contactData.phone,
            },
          });
        } catch (err) {
          console.error("Error updating lead with contact:", err);
        }
      }
    };

    const timeoutId = setTimeout(updateLeadWithContact, 1000);
    return () => clearTimeout(timeoutId);
  }, [
    contactData.firstName,
    contactData.lastName,
    contactData.email,
    contactData.phone,
    leadId,
  ]);

  const handleSubmit = async () => {
    if (selectedFeatures.length === 0) {
      setError(t("start.features.errors.atLeastOne"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let logoUrl = null;

      // Upload logo to Firebase Storage if provided
      if (logoFile) {
        const timestamp = Date.now();
        const fileName = `logos/${timestamp}_${logoFile.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, logoFile);
        logoUrl = await getDownloadURL(storageRef);
      }

      let finalLeadId = leadId;

      // If we have a leadId, update the existing lead
      if (leadId) {
        await updateDoc(doc(db, "leads", leadId), {
          organization: {
            name: organizationData.name,
            type: organizationData.type,
            country: organizationData.country,
            estimatedUsers: organizationData.estimatedUsers,
          },
          contact: {
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            email: contactData.email,
            phone: contactData.phone,
          },
          features: selectedFeatures,
          logoUrl,
          completedAt: serverTimestamp(),
          status: "new",
        });
      } else {
        // Fallback: create a new lead if somehow leadId doesn't exist
        const docRef = await addDoc(collection(db, "leads"), {
          organization: {
            name: organizationData.name,
            type: organizationData.type,
            country: organizationData.country,
            estimatedUsers: organizationData.estimatedUsers,
          },
          contact: {
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            email: contactData.email,
            phone: contactData.phone,
          },
          features: selectedFeatures,
          logoUrl,
          createdAt: serverTimestamp(),
          status: "new",
        });
        finalLeadId = docRef.id;
      }

      // Send email with status link
      const statusUrl = `${window.location.origin}/status/${finalLeadId}`;
      try {
        await fetch("/api/send-email", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: contactData.email,
            firstName: contactData.firstName,
            organizationName: organizationData.name,
            statusUrl,
          }),
        });
      } catch (emailError) {
        console.error("Error sending email:", emailError);
        // Don't block the redirect if email fails
      }

      // Redirect to status page
      router.push(`/status/${finalLeadId}`);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(
        err instanceof Error ? err.message : t("start.errors.submitFailed")
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex flex-col h-dvh w-full md:items-center md:justify-center bg-slate-100 px-0">
      <Head>
        <title>{t("start.title")}</title>
        <meta name="description" content={t("start.description")} />
      </Head>
      {/* Language Selector */}
      {!isRegionLoading && region !== "US" && (
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={() => setLanguage(language === "fr" ? "en" : "fr")}
            aria-label={`Switch to ${
              language === "fr" ? "English" : "French"
            }`}
            className="px-4 py-2 bg-white rounded-full border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200 font-medium shadow-sm"
          >
            {language === "fr" ? "EN" : "FR"}
          </button>
        </div>
      )}

      {/* Progress indicator */}
      <div className="flex items-center justify-center my-4 md:mt-0 md:mb-8 w-full max-w-screen-2xl px-4 md:px-0">
        <div className="flex items-center gap-1 sm:gap-2">
          <div
            className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ${
              currentStep === "organization"
                ? "bg-blue-600 text-white"
                : "bg-blue-600 text-white"
            }`}
          >
            1
          </div>
          <div className="h-1 w-8 sm:w-12 bg-slate-200">
            <div
              className={`h-full ${
                currentStep !== "organization" ? "bg-blue-600" : "bg-slate-200"
              }`}
            />
          </div>
          <div
            className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ${
              currentStep === "contact"
                ? "bg-blue-600 text-white"
                : currentStep === "features"
                ? "bg-blue-600 text-white"
                : "bg-slate-200 text-slate-600"
            }`}
          >
            2
          </div>
          <div className="h-1 w-8 sm:w-12 bg-slate-200">
            <div
              className={`h-full ${
                currentStep === "features" ? "bg-blue-600" : "bg-slate-200"
              }`}
            />
          </div>
          <div
            className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ${
              currentStep === "features"
                ? "bg-blue-600 text-white"
                : "bg-slate-200 text-slate-600"
            }`}
          >
            3
          </div>
        </div>
      </div>
      <div className="flex flex-col w-full md:flex-row overflow-hidden max-w-screen-2xl h-[calc(100vh-120px)] max-md:h-full md:h-[800px] md:rounded-[46px] mx-auto shadow-2xl shadow-slate-300/60 md:scale-90 presentation-app 2xl:scale-100">
        {/* FeatcalApp Preview - Mobile: Top, Desktop: Right */}
        <div className="relative z-0 flex-shrink-0 md:flex-1 md:p-3 bg-white order-1 md:order-2 h-[35%] md:h-auto min-h-[200px]">
          <div className="bg-white h-full w-full flex overflow-hidden md:rounded-[38px]">
            <div className="h-full w-full bg-slate-100 flex overflow-hidden relative items-center justify-center">
              {/* Mobile Feature Cards - Shows in preview area on mobile */}
              {currentStep === "features" && selectedFeatures.length > 0 && (
                <div className="absolute inset-0 md:hidden overflow-y-auto p-4 bg-slate-100 z-50">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {t("start.features.selectedFeatures")}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedFeatures.includes("nutrition") && (
                      <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-2.5 shadow-lg">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-white/20 rounded-lg p-1.5 backdrop-blur-sm mb-1.5">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                              />
                            </svg>
                          </div>
                          <h4 className="text-white font-semibold text-xs leading-tight">
                            {t("start.features.cards.nutrition.title")}
                          </h4>
                        </div>
                      </div>
                    )}
                    {selectedFeatures.includes("sleep") && (
                      <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-2.5 shadow-lg">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-white/20 rounded-lg p-1.5 backdrop-blur-sm mb-1.5">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                              />
                            </svg>
                          </div>
                          <h4 className="text-white font-semibold text-xs leading-tight">
                            {t("start.features.cards.sleep.title")}
                          </h4>
                        </div>
                      </div>
                    )}
                    {selectedFeatures.includes("exercise") && (
                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-2.5 shadow-lg">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-white/20 rounded-lg p-1.5 backdrop-blur-sm mb-1.5">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 10V3L4 14h7v7l9-11h-7z"
                              />
                            </svg>
                          </div>
                          <h4 className="text-white font-semibold text-xs leading-tight">
                            {t("start.features.cards.exercise.title")}
                          </h4>
                        </div>
                      </div>
                    )}
                    {selectedFeatures.includes("stress") && (
                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-2.5 shadow-lg">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-white/20 rounded-lg p-1.5 backdrop-blur-sm mb-1.5">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <h4 className="text-white font-semibold text-xs leading-tight">
                            {t("start.features.cards.stress.title")}
                          </h4>
                        </div>
                      </div>
                    )}
                    {selectedFeatures.includes("recipes") && (
                      <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg p-2.5 shadow-lg">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-white/20 rounded-lg p-1.5 backdrop-blur-sm mb-1.5">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                              />
                            </svg>
                          </div>
                          <h4 className="text-white font-semibold text-xs leading-tight">
                            {t("start.features.cards.recipes.title")}
                          </h4>
                        </div>
                      </div>
                    )}
                    {selectedFeatures.includes("ai_logging") && (
                      <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg p-2.5 shadow-lg">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-white/20 rounded-lg p-1.5 backdrop-blur-sm mb-1.5">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                              />
                            </svg>
                          </div>
                          <h4 className="text-white font-semibold text-xs leading-tight">
                            {t("start.features.cards.ai_logging.title")}
                          </h4>
                        </div>
                      </div>
                    )}
                    {selectedFeatures.includes("consultation_notes") && (
                      <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg p-2.5 shadow-lg">
                        <div className="flex flex-col items-center text-center">
                          <div className="bg-white/20 rounded-lg p-1.5 backdrop-blur-sm mb-1.5">
                            <svg
                              className="w-5 h-5 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                          </div>
                          <h4 className="text-white font-semibold text-xs leading-tight">
                            {t("start.features.cards.consultation_notes.title")}
                          </h4>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/** Feature Cards - Desktop only (Appear when features are selected) **/}

              {/** Nutrition Tracking Card **/}
              {currentStep === "features" &&
                selectedFeatures.includes("nutrition") && (
                  <div
                    ref={nutritionCardRef}
                    className="absolute top-[15%] right-[8%] z-40 hidden md:block"
                  >
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-4 shadow-xl shadow-emerald-500/30 w-64 border border-emerald-400/20">
                      <div className="flex items-start gap-3">
                        <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-sm">
                            {t("start.features.cards.nutrition.title")}
                          </h3>
                          <p className="text-emerald-100 text-xs mt-1">
                            {t("start.features.cards.nutrition.subtitle")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/** Sleep Tracking Card **/}
              {currentStep === "features" &&
                selectedFeatures.includes("sleep") && (
                  <div
                    ref={sleepCardRef}
                    className="absolute top-[25%] left-[5%] z-40 hidden md:block"
                  >
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl p-4 shadow-xl shadow-indigo-500/30 w-60 border border-indigo-400/20">
                      <div className="flex items-start gap-3">
                        <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-sm">
                            {t("start.features.cards.sleep.title")}
                          </h3>
                          <p className="text-indigo-100 text-xs mt-1">
                            {t("start.features.cards.sleep.subtitle")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/** Exercise Card **/}
              {currentStep === "features" &&
                selectedFeatures.includes("exercise") && (
                  <div
                    ref={exerciseCardRef}
                    className="absolute bottom-[20%] right-[10%] z-40 hidden md:block"
                  >
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-4 shadow-xl shadow-orange-500/30 w-56 border border-orange-400/20">
                      <div className="flex items-start gap-3">
                        <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-sm">
                            {t("start.features.cards.exercise.title")}
                          </h3>
                          <p className="text-orange-100 text-xs mt-1">
                            {t("start.features.cards.exercise.subtitle")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/** Stress & Mood Card **/}
              {currentStep === "features" &&
                selectedFeatures.includes("stress") && (
                  <div
                    ref={stressCardRef}
                    className="absolute top-[50%] left-[8%] z-40 hidden md:block"
                  >
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 shadow-xl shadow-purple-500/30 w-58 border border-purple-400/20">
                      <div className="flex items-start gap-3">
                        <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-sm">
                            {t("start.features.cards.stress.title")}
                          </h3>
                          <p className="text-purple-100 text-xs mt-1">
                            {t("start.features.cards.stress.subtitle")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/** Recipes Card **/}
              {currentStep === "features" &&
                selectedFeatures.includes("recipes") && (
                  <div
                    ref={recipesCardRef}
                    className="absolute top-[10%] left-[12%] z-40 hidden md:block"
                  >
                    <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-4 shadow-xl shadow-rose-500/30 w-60 border border-rose-400/20">
                      <div className="flex items-start gap-3">
                        <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-sm">
                            {t("start.features.cards.recipes.title")}
                          </h3>
                          <p className="text-rose-100 text-xs mt-1">
                            {t("start.features.cards.recipes.subtitle")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/** AI Food Logging Card **/}
              {currentStep === "features" &&
                selectedFeatures.includes("ai_logging") && (
                  <div
                    ref={aiLoggingCardRef}
                    className="absolute bottom-[15%] left-[6%] z-40 hidden md:block"
                  >
                    <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl p-4 shadow-xl shadow-cyan-500/30 w-64 border border-cyan-400/20">
                      <div className="flex items-start gap-3">
                        <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-sm">
                            {t("start.features.cards.ai_logging.title")}
                          </h3>
                          <p className="text-cyan-100 text-xs mt-1">
                            {t("start.features.cards.ai_logging.subtitle")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/** Consultation Notes Card **/}
              {currentStep === "features" &&
                selectedFeatures.includes("consultation_notes") && (
                  <div
                    ref={consultationCardRef}
                    className="absolute top-[40%] right-[6%] z-40 hidden md:block"
                  >
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-4 shadow-xl shadow-amber-500/30 w-64 border border-amber-400/20">
                      <div className="flex items-start gap-3">
                        <div className="bg-white/20 rounded-xl p-2 backdrop-blur-sm">
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-white font-semibold text-sm">
                            {t("start.features.cards.consultation_notes.title")}
                          </h3>
                          <p className="text-amber-100 text-xs mt-1">
                            {t(
                              "start.features.cards.consultation_notes.subtitle"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {/** Main Phone Preview **/}
              <div
                className={`w-[450px] border border-slate-300 relative rounded-4xl overflow-hidden h-[1000px] shadow-2xl shadow-slate-300/60 ${
                  logoPreview || currentStep === "features"
                    ? "mt-160 md:mt-100 scale-85 md:scale-100"
                    : "mt-0 scale-25 md:scale-70"
                } transition-all duration-300`}
              >
                <FeatcalApp
                  logo={displayLogo || "/images/logo.svg"}
                  showLogoSkeleton={showLogoSkeleton}
                />
              </div>
              <div
                className={`bg-gradient-to-t from-slate-100 to-transparent ${
                  logoPreview ? "opacity-100" : "opacity-0"
                } absolute bottom-0 left-0 w-full h-40 z-[50]`}
              ></div>
            </div>
          </div>
        </div>

        {/* Form - Mobile: Bottom, Desktop: Left */}
        <div className="relative z-50 w-full md:w-[515px] order-2 md:order-1 h-[65%] md:h-auto overflow-y-auto flex-shrink-0">
          <div className="bg-white p-4 sm:p-6 md:p-8 lg:p-10 z-10 h-full flex flex-col max-md:border-t max-md:border-slate-200">
            {/* Step 1: Organization Info */}
            {currentStep === "organization" && (
              <div className="space-y-4 sm:space-y-6 w-full flex-1 overflow-y-auto">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                    {t("start.organization.title")}
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-600">
                    {t("start.organization.description")}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("start.organization.name")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={organizationData.name}
                    onChange={(e) =>
                      setOrganizationData({
                        ...organizationData,
                        name: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-non focus:border-blue-500"
                    placeholder="e.g., Wellness Clinic"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("start.organization.type")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={organizationData.type}
                    onChange={(e) =>
                      setOrganizationData({
                        ...organizationData,
                        type: e.target.value as OrganizationType,
                      })
                    }
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-non focus:border-blue-500"
                  >
                    <option value="clinic">
                      {t("start.organization.types.clinic")}
                    </option>
                    <option value="nutritionist">
                      {t("start.organization.types.nutritionist")}
                    </option>
                    <option value="coach">
                      {t("start.organization.types.coach")}
                    </option>
                    <option value="other">
                      {t("start.organization.types.other")}
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("start.organization.country")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={organizationData.country}
                    onChange={(e) =>
                      setOrganizationData({
                        ...organizationData,
                        country: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-non focus:border-blue-500"
                  >
                    <option value="">
                      {t("start.organization.selectCountry")}
                    </option>
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("start.organization.estimatedUsers")}
                  </label>
                  <input
                    type="number"
                    value={organizationData.estimatedUsers}
                    onChange={(e) =>
                      setOrganizationData({
                        ...organizationData,
                        estimatedUsers: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-non focus:border-blue-500"
                    min="1"
                  />
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("start.organization.logo")}
                  </label>
                  <div className="mt-2">
                    <div className="flex items-center gap-4">
                      {logoPreview ? (
                        <div className="relative">
                          <Image
                            src={logoPreview}
                            alt="Logo preview"
                            width={96}
                            height={96}
                            className="w-24 h-24 object-contain rounded-xl border-2 border-slate-200 p-2"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              // Clear any pending timeout
                              if (logoTimeoutRef.current) {
                                clearTimeout(logoTimeoutRef.current);
                                logoTimeoutRef.current = null;
                              }
                              setLogoFile(null);
                              setLogoPreview(null);
                              setDisplayLogo("/images/logo.svg");
                              setShowLogoSkeleton(false);
                              // Reset input file to allow selecting the same file again
                              if (logoInputRef.current) {
                                logoInputRef.current.value = "";
                              }
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
                          <svg
                            className="w-8 h-8 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1">
                        <label
                          htmlFor="logo-upload"
                          className="cursor-pointer inline-flex items-center px-4 py-2 border border-slate-300 rounded-xl text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                        >
                          {logoPreview
                            ? t("start.organization.changeLogo")
                            : t("start.organization.uploadLogo")}
                        </label>
                        <input
                          id="logo-upload"
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="hidden"
                        />
                        <p className="text-xs text-slate-500 mt-2">
                          {t("start.organization.logoFormat")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Contact Info */}
            {currentStep === "contact" && (
              <div className="space-y-4 sm:space-y-6 w-full flex-1 overflow-y-auto">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                    {t("start.contact.title")}
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-600">
                    {t("start.contact.description")}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("start.contact.firstName")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={contactData.firstName}
                      onChange={(e) =>
                        setContactData({
                          ...contactData,
                          firstName: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-non focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("start.contact.lastName")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={contactData.lastName}
                      onChange={(e) =>
                        setContactData({
                          ...contactData,
                          lastName: e.target.value,
                        })
                      }
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-non focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("start.contact.email")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={contactData.email}
                    onChange={(e) =>
                      setContactData({ ...contactData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-non focus:border-blue-500"
                    placeholder={t("start.contact.emailPlaceholder")}
                  />
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone number (optional)
                  </label>
                  <input
                    type="tel"
                    value={contactData.phone}
                    onChange={(e) =>
                      setContactData({ ...contactData, phone: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-non focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div> */}
              </div>
            )}

            {/* Step 3: Feature Selection */}
            {currentStep === "features" && (
              <div className="space-y-4 sm:space-y-6 w-full flex-1 overflow-y-auto">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                    {t("start.features.title")}
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-600">
                    {t("start.features.description")}
                  </p>
                </div>

                <div className="space-y-3">
                  {(
                    [
                      "nutrition",
                      "sleep",
                      "exercise",
                      "stress",
                      "recipes",
                      "ai_logging",
                      "consultation_notes",
                    ] as FeatureType[]
                  ).map((featureId) => (
                    <button
                      key={featureId}
                      onClick={() => toggleFeature(featureId)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                        selectedFeatures.includes(featureId)
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 ${
                            selectedFeatures.includes(featureId)
                              ? "bg-blue-500 border-blue-500"
                              : "border-slate-300"
                          }`}
                        >
                          {selectedFeatures.includes(featureId) && (
                            <svg
                              className="w-full h-full text-white"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">
                            {t(`start.features.options.${featureId}.label`)}
                          </div>
                          <div className="text-sm text-slate-600 mt-0.5">
                            {t(
                              `start.features.options.${featureId}.description`
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mt-4 sm:mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-8 w-full flex-shrink-0 pt-4 border-t border-slate-200">
              {currentStep !== "organization" && (
                <button
                  onClick={handleBack}
                  disabled={isLoading}
                  className="px-4 sm:px-6 py-2.5 sm:py-3 border-2 border-slate-200 rounded-full text-sm sm:text-base font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("start.buttons.back")}
                </button>
              )}
              {currentStep !== "features" ? (
                <button
                  onClick={handleNext}
                  className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-full text-sm sm:text-base font-medium hover:bg-blue-700 transition-colors"
                >
                  {t("start.buttons.continue")}
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || selectedFeatures.length === 0}
                  className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-full text-sm sm:text-base font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white"></div>
                      <span className="text-xs sm:text-base">
                        {t("start.buttons.submitting")}
                      </span>
                    </span>
                  ) : (
                    t("start.buttons.submit")
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
